<template>
  <div>
    <app-spinner v-if="!is_page_loaded" />
    <template v-else>
      <app-cells>
        <h1 @click="onCopyToClipboard()" class="title title--hovered" style="cursor: pointer">
          {{ meal_data.hotel.name }} (Meal #{{ $route.params.id }})
        </h1>
        <div v-if="meal_data.lead.reservation_manager" class="manager-title">
          <div class="manager-title__photo">
            <img :src="getUserAvatar(meal_data.lead.reservation_manager.crop.crop_url)" alt="Avatar">
          </div>
          <span class="manager-title__name">{{ meal_data.lead.reservation_manager.full_name }}</span>
        </div>
        <app-option-picker
          @click="onMealOptionSelected($event)"
          v-model="meal_data.reservation_status"
          :options="meal_data.reservation_status.available_reservation_statuses_for_meal"
          :disable="meal_data.reservation_status.available_reservation_statuses_for_meal?.length < 1 || (!is_general_manager && !is_reservations_department)"
        />
        <div v-if="meal_data.lead" class="manager-title">
          <div class="manager-title__photo">
            <img :src="getUserAvatar(meal_data.lead.manager.crop.crop_url)" alt="Avatar">
          </div>
          <span class="manager-title__name">{{ meal_data.lead.manager.full_name }}</span>
        </div>
        <app-option-picker v-model="meal_data.sales_status" disable />
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card info-cards__card--nominh">
          <div class="info-cards__row info-cards__row--vertical-center">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Group name:</span>
              <router-link
                v-if="meal_data.lead"
                :to="{ name: 'leads-detail-reservations', params: { id: meal_data.lead.id } }"
                class="info-cards__link"
              >
                {{ meal_data.lead.name ? meal_data.lead.name : `#${meal_data.lead?.id}` }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <app-info-text-panel v-if="meal_data.already_booked">
        Sales manager marked this reservation as already booked.
      </app-info-text-panel>
      <app-page-container extra>
        <app-colored-notes
          v-if="(is_general_manager || is_reservations_department) && meal_data.notes"
          color="#77b017"
          :text="meal_data.notes"
        />
        <app-form-group v-if="!is_reservations_department" label="Sales manager notes for the hotel" label-for="notes">
          <app-textarea
            @change.native="onPatchMealReservationNotes"
            v-model="meal_data.notes"
            rows="5"
            placeholder="Write a note"
            id="notes"
            :readonly="(!is_general_manager && !is_sales_manager && !is_sales_supervisor) && meal_data.reservation_status.code === 'booked'"
          />
        </app-form-group>
        <template v-if="is_general_manager && !meal_data.clients_assignment">
          <app-flex-grid
            :rows="[[1, '130px', '80px', '80px', '80px', 'auto']]"
            align-items="flex-end"
            margin-top="25px"
          >
            <template #header-1>
              <h3 class="title title--mb15">Meals configuration</h3>
            </template>
            <template #col-1-1>
              <app-form-group label="Type" class="form-group--no-indent">
                <app-select
                  v-model="form_meal_config.meal_type"
                  :options="meal_type_options"
                  :clearable="false"
                  label="name"
                  placeholder="Choose type"
                />
              </app-form-group>
            </template>
            <template #col-1-2>
              <app-form-group label="Date" class="form-group--no-indent">
                <app-date-picker
                  v-model="form_meal_config.meal_date"
                  placeholder="Date"
                />
              </app-form-group>
            </template>
            <template #col-1-3>
              <app-form-group label="Time" class="form-group--no-indent">
                <app-input
                  v-model="form_meal_config.meal_time"
                  v-mask="'99:99'"
                  placeholder="00:00"
                  autocomplete="off"
                  id="time"
                  name="time"
                  @paste.native.prevent
                />
              </app-form-group>
            </template>
            <template #col-1-4>
              <app-form-group label="Clients" label-for="add_meal_clients" class="form-group--no-indent">
                <app-input
                  v-model.number="form_meal_config.meal_clients"
                  type="number"
                  name="add_meal_clients"
                  id="add_meal_clients"
                  placeholder=""
                />
              </app-form-group>
            </template>
            <template #col-1-5>
              <app-form-group label="Crew" label-for="add_meal_crew" class="form-group--no-indent">
                <app-input
                  v-model.number="form_meal_config.meal_crew"
                  type="number"
                  name="add_meal_crew"
                  id="add_meal_crew"
                  placeholder=""
                />
              </app-form-group>
            </template>
            <template #col-1-6>
              <app-button @click.native="onClickAddMealConfig" :disabled="!form_meal_config.meal_type">
                Add
              </app-button>
            </template>
          </app-flex-grid>
          <app-cells position="end" :indent="false">
            <app-button @click.native="onUpdateMealsRequests" ref="save_meals">Save</app-button>
          </app-cells>
        </template>
        <template v-if="meal_data.requests.length">
          <h3 class="title title--mb15">Meals</h3>
          <app-cells-grid :columns="1" gap="0">
            <AppMealCard
              v-for="(card, index) in meal_data.requests"
              :key="card.id || card.__key"
              @changeFormElement="(value, element) => onChangeMealFormElement(index, value, element)"
              @changeMealClients="onChangeMealClients($event, index)"
              @cardDelete="onCardMealDelete(index)"
              :meal="card"
              :order-number="index + 1"
              :clients-options="clients_options"
              :is-deletable="!meal_data.clients_assignment && is_general_manager"
              :is-select-visible="!meal_data.clients_assignment"
              :is-editable="is_general_manager"
            />
          </app-cells-grid>
        </template>
        <div v-if="meal_data.sales_status.code === 'book'" class="downloads downloads--mb">
          <div class="downloads__item" :class="{'downloads__item--loading': clients_assignment_loader}">
            <div class="downloads__left">
              <div class="downloads__info">
                <span class="downloads__name downloads__name--no-events">Clients assignment completed</span>
              </div>
            </div>
            <div class="downloads__right">
              <div class="downloads__values">
                <span class="downloads__key">Status:</span>
                <app-switcher
                  @change="onUpdateClientsAssignment"
                  v-model="meal_data.clients_assignment"
                  :readonly="(!is_general_manager && !is_sales_manager && !is_sales_supervisor) && !meal_data.lead?.client_group"
                />
              </div>
              <template v-if="meal_data.clients_assignment && meal_data.clients_assignment_by && !clients_assignment_loader">
                <div class="downloads__values">
                  <span class="downloads__key">Completed at:</span>
                  <span class="downloads__value downloads__width">
                          {{ dateTimeFormat(meal_data.clients_assignment_at) }}</span>
                </div>
                <div class="downloads__values">
                  <span class="downloads__key">Completed by:</span>
                  <div class="downloads__person downloads__width">
                    <div class="downloads__photo">
                      <img :src="getUserAvatar(meal_data.clients_assignment_by?.crop.crop_url)" alt="Person">
                    </div>
                    <span class="downloads__value">{{ meal_data.clients_assignment_by?.full_name || '-' }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </app-page-container>
      <app-tabs :tabs="tabs" />
      <transition name="fade" mode="out-in">
        <router-view :meal-data="meal_data" />
      </transition>
    </template>
    <modal
      name="meal-not-available-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="closeMealNotAvailablePopup" class="popup__close" type="button"></button>
        <div class="popup__title">Not available</div>
        <div class="popup__body">
          <app-form-group label="Additional message to Sales" label-for="notes">
            <app-textarea
              v-model="meal_additional_message"
              rows="5"
              placeholder="Write a message"
              id="notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onClickSaveMealNotAvailableButton" ref="submit_meal_not_available">
              Save
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="meal-booked-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="closeMealBookedPopup" class="popup__close" type="button"></button>
        <div class="popup__title">Booked</div>
        <div class="popup__body">
          <app-form-group label="Additional message to Sales" label-for="meal_booked_notes">
            <app-textarea
              v-model="form_meal_booked.additional_message"
              rows="5"
              placeholder="Write a message"
              id="meal_booked_notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onClickSaveMealBookedButton" ref="submit_meal_booked">
              Save
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getReservationsHotelsClients,
  getReservationsMeal,
  getReservationsMealType,
  patchReservationsMeal
} from '@/http'
import AppTabs from '@/components/AppTabs.vue'
import AppOptionPicker from '@/components/AppOptionPicker.vue'
import AppColoredNotes from '@/components/AppColoredNotes.vue'
import { format, parseISO } from 'date-fns'
import AppMealCard from '@/components/AppMealCard.vue'
import AppInfoTextPanel from '@/components/AppInfoTextPanel.vue'

export default {
  name: 'ReservationMealLayout',
  components: { AppInfoTextPanel, AppMealCard, AppColoredNotes, AppOptionPicker, AppTabs },
  data() {
    return {
      is_page_loaded: false,
      tabs: [
        { name: 'reservation-meal-general', label: 'General' },
        { name: 'reservation-meal-finance', label: 'Finance' },
        { name: 'reservation-meal-emails', label: 'Emails' },
        { name: 'reservation-meal-history', label: 'History' },
        { name: 'reservation-meal-clients', label: 'Clients' },
      ],
      meal_data: {},
      meal_additional_message: '',
      form_meal_booked: {},
      clients_options: [],
      meal_type_options: [],
      clients_assignment_loader: false,
      form_meal_config: {},
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_reservations_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_assistant')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
  },
  created() {
    this.getMealData()
    getReservationsMealType().then(res => { this.meal_type_options = res.data })
  },
  methods: {
    onClickAddMealConfig() {
      const data = {
        __key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: this.form_meal_config.meal_type,
        date: this.form_meal_config.meal_date,
        time: this.form_meal_config.meal_time,
        clients: this.form_meal_config.meal_clients,
        crew: this.form_meal_config.meal_crew,
        clients_list: [],
        is_cancelled: false,
      }

      this.meal_data.requests.push(data)
      this.form_meal_config.meal_type = null
      this.form_meal_config.meal_time = null
      this.form_meal_config.meal_clients = null
      this.form_meal_config.meal_crew = null
    },
    onCardMealDelete(index) {
      this.meal_data.requests.splice(index, 1)
    },
    onChangeMealClients(clients, index) {
      this.meal_data.requests[index].clients_list = clients
    },
    onChangeMealFormElement(index, value, element) {
      this.meal_data.requests[index][element] = value
    },
    onUpdateClientsAssignment() {
      this.clients_assignment_loader = true
      let meal_requests = []

      if (this.meal_data.requests) {
        meal_requests = this.meal_data.requests
          .map(item => (
            {
              id: item?.id,
              clients_list: item.clients_list.map(client => client.id)
            }
          ))
      }

      patchReservationsMeal(
        +this.$route.params.id,
        { clients_assignment: this.meal_data.clients_assignment, requests: meal_requests }
      )
        .then(() => {
          this.getMealData()
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.clients_assignment_loader = false
        })
        .catch(() => {
          this.meal_data.clients_assignment = !this.meal_data.clients_assignment
          this.clients_assignment_loader = false
        })
    },
    onUpdateMealsRequests() {
      this.$refs.save_meals.preload = true
      let meal_requests = []

      if (this.meal_data.requests) {
        meal_requests = this.meal_data.requests
          .map(item => (
            {
              id: item?.id,
              clients_list: item.clients_list ? item.clients_list.map(client => client.id) : null
            }
          ))
        if (this.is_general_manager) {
          meal_requests = this.meal_data.requests
            .map(item => (
              {
                id: item?.id,
                clients_list: item.clients_list ? item.clients_list.map(client => client.id) : null,
                date: item.date,
                time: item.time,
                clients: item.clients,
                crew: item.crew,
                type: item.type.code,
              }
            ))
        }
      }

      patchReservationsMeal(
        +this.$route.params.id,
        { requests: meal_requests }
      )
        .finally(() => { this.$refs.save_meals.preload = false })
        .then(() => {
          this.getMealData()
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
        })
    },
    onPatchMealReservationNotes() {
      patchReservationsMeal(+this.$route.params.id, { notes: this.meal_data.notes })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.getMealData()
        })
    },
    onClickSaveMealBookedButton() {
      this.sendMealBookedForm()
    },
    sendMealBookedForm() {
      this.$refs.submit_meal_booked.preload = true
      patchReservationsMeal(+this.$route.params.id, this.normalizeMealBookedForm())
        .finally(() => { this.$refs.submit_meal_booked.preload = false })
        .then(() => {
          this.getMealData()
          this.$modal.hide('meal-booked-popup')
        })
    },
    normalizeMealBookedForm() {
      const normalizedForm = {}

      normalizedForm.reservation_status = 'booked'
      normalizedForm.additional_message = this.form_meal_booked.additional_message

      return normalizedForm
    },
    closeMealBookedPopup() {
      this.is_page_loaded = false
      this.$modal.hide('meal-booked-popup')
      this.getMealData()
    },
    onClickSaveMealNotAvailableButton() {
      this.$refs.submit_meal_not_available.preload = true
      patchReservationsMeal(
        +this.$route.params.id,
        { notes: this.meal_additional_message, reservation_status: 'not_available' }
      )
        .finally(() => { this.$refs.submit_meal_not_available.preload = false })
        .then(() => {
          this.getMealData()
          this.$modal.hide('meal-not-available-popup')
        })
    },
    getMealData() {
      getReservationsMeal(this.$route.params.id).then(res => {
        this.meal_data = res.data
        this.form_meal_config.meal_date = res.data.min_date
        if (!this.meal_data.clients_assignment) this.tabs.pop()
        document.title = `${ this.meal_data.hotel.name } (Meal #${ this.$route.params.id }) | Altezza Board`
        if (this.meal_data.lead?.client_group?.id) {
          getReservationsHotelsClients(
            this.meal_data.lead.client_group.id,
            { check_in: this.meal_data.min_date, check_out: this.meal_data.max_date, hotel_id: this.meal_data.hotel.id }
          ).then(res => { this.clients_options = res.data })
        }
        this.is_page_loaded = true
      })
    },
    closeMealNotAvailablePopup() {
      this.is_page_loaded = false
      this.$modal.hide('meal-not-available-popup')
      this.getMealData()
    },
    onMealOptionSelected({ code }) {
      if (code === 'not_available') {
        this.meal_additional_message = ''
        this.$modal.show('meal-not-available-popup')
      } else if (code === 'booked') {
        this.form_meal_booked = {}
        this.$modal.show('meal-booked-popup')
      }
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onCopyToClipboard() {
      navigator.clipboard.writeText(location.href)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  }
}
</script>
